import { OffersResponse, LocalOffersResponse, Offer, Category, Location } from '../types/offers';
import { sentry } from '../services/sentry';

const API_BASE_URL = import.meta.env.VITE_API_URL

const filterInvalidOffers = (offers: Offer[]): Offer[] => {
  return offers.filter((offer) => {
    const { type, value } = offer.attributes.user_reward;
    if (type === 'PERCENT' && value < 1) {
      sentry.captureError(
        new Error(`Filtered invalid offer: reward value is below minimum threshold`),
        { context: { offerId: offer.id, offerName: offer.attributes.name, rewardType: type, rewardValue: value } }
      );
      return false;
    }
    return true;
  });
};

/**
 * Determines the API base URL based on the current hostname
 * Maps domains according to the following pattern:
 * - rewards.dev.empower.me → api.dev.empower.me
 * - rewards.empower.me → api.empower.me
 * - rewards.dev.tiltmoney.com → api.dev.tiltmoney.com
 * - rewards.tiltmoney.com → api.tiltmoney.com
 */
const getApiBaseUrl = (): string => {
  // Fallback to environment variable if running in a non-browser environment
  if (typeof window === 'undefined') {
    return import.meta.env.VITE_API_URL;
  }

  const hostname = window.location.hostname;
  console.log('Current hostname:', hostname);

  try {
    // Check if hostname contains empower.me or tiltmoney.com
    if (hostname.includes('empower.me')) {
      // Check if it's a dev environment
      if (hostname.includes('dev.')) {
        return 'https://api.dev.empower.me';
      }
      return 'https://api.empower.me';
    } else if (hostname.includes('tiltmoney.com')) {
      // Check if it's a dev environment
      if (hostname.includes('dev.')) {
        return 'https://api.dev.tiltmoney.com';
      }
      return 'https://api.tiltmoney.com';
    }

    // Fallback to environment variable if hostname doesn't match expected patterns
    sentry.captureError(new Error('Unknown hostname pattern, falling back to environment variable'), {
      context: { hostname }
    });
    return import.meta.env.VITE_API_URL;
  } catch (error) {
    console.error('Error determining API base URL:', error);
    // Fallback to environment variable in case of any errors
    return import.meta.env.VITE_API_URL;
  }
};

export const getOffers = async (): Promise<OffersResponse> => {

  const apiBaseUrl = getApiBaseUrl();
  try {
    const response = await fetch(`${apiBaseUrl}/api/creditcard/kard/offers`, {
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include'
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error:', {
        status: response.status,
        statusText: response.statusText,
        body: errorText
      });
      throw new Error(`Failed to fetch offers: ${response.statusText}`);
    }

    const data: OffersResponse = await response.json();
    return { ...data, data: filterInvalidOffers(data.data) };
  } catch (error) {
    console.error('Error in getOffers:', error);
    throw error;
  }
};

export const transformLocalOffersResponse = (data: LocalOffersResponse): OffersResponse => {
  const locations = data.data;
  const included = data.included;

  const offersMap = new Map<string, Offer>();
  const categoriesMap = new Map<string, Category>();

  included.forEach((item) => {
    if (item.type === 'standardOffer') {
      offersMap.set(item.id, item as Offer);
    } else if (item.type === 'category') {
      categoriesMap.set(item.id, item as Category);
    }
  });

  const transformedOffers: Offer[] = [];

  locations.forEach((location: Location) => {
    const offerIds = location.relationships.offers.data.map((offer) => offer.id);

    offerIds.forEach((offerId) => {
      const offer = offersMap.get(offerId);
      if (offer) {
        const transformedOffer: Offer = {
          ...offer,
          attributes: {
            ...offer.attributes,
            location: location.attributes.address,
            is_local: true,
            locationId: location.id,
          },
          // Copy the category relationships from the location to the offer
          // because the API doesn't include relationships on the offer objects
          relationships: {
            category: location.relationships.category
          }
        };

        transformedOffers.push(transformedOffer);
      }
    });
  });

  return {
    data: filterInvalidOffers(transformedOffers),
    links: data.links,
    included: Array.from(categoriesMap.values()),
  };
};

export const getLocalOffers = async (): Promise<OffersResponse> => {
  const apiBaseUrl = getApiBaseUrl();

  try {
    const response = await fetch(
      `${apiBaseUrl}/api/creditcard/kard/localOffers`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Local Offers API Error:', {
        status: response.status,
        statusText: response.statusText,
        body: errorText
      });
      throw new Error(`Failed to fetch local offers: ${response.statusText}`);
    }

    const data: LocalOffersResponse = await response.json();
    return transformLocalOffersResponse(data);
  } catch (error) {
    console.error('Error in getLocalOffers:', error);
    throw error;
  }
};
